import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, FileText, User, Calendar, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParentDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    pupil_name: '',
    dob: '',
    place_of_birth: '',
    father_name: '',
    father_occupation: '',
    father_income: '',
    father_qualification: '',
    father_contact: '',
    father_email: '',
    address: '',
    father_aadhar: '',
    mother_name: '',
    mother_qualification: '',
    mother_contact: '',
    mother_email: '',
    religion_caste: '',
    vaccinated: false,
    class_sought: '',
    mother_tongue: '',
    mother_aadhar: '',
    medium_of_instruction: '',
    sats_no: '',
    no_of_dependents: '',
    staying_with: '',
    identification_mark_1: '',
    identification_mark_2: '',
    siblings_info: ''
  });
  const [files, setFiles] = useState({
    birth_certificate: null,
    child_aadhar: null,
    father_aadhar: null,
    mother_aadhar: null,
    photo: null
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/');
      return;
    }

    setUser(JSON.parse(userData));
    fetchApplication(token);
  }, [navigate]);

  const fetchApplication = async (token) => {
    try {
      const response = await axios.get(`${API}/applications/my-application`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplication(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setShowForm(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    if (selectedFiles && selectedFiles[0]) {
      setFiles(prev => ({
        ...prev,
        [name]: selectedFiles[0]
      }));
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    
    if (!formData.pupil_name || !formData.father_name || !formData.mother_name) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      formDataToSend.append('data', JSON.stringify(formData));
      
      Object.keys(files).forEach(key => {
        if (files[key]) {
          formDataToSend.append(key, files[key]);
        }
      });

      const response = await axios.post(`${API}/applications`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success(`Application submitted successfully! Your Admission Number: ${response.data.admission_no}`);
        setShowForm(false);
        fetchApplication(token);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F9FAFB' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b" style={{ borderColor: '#E5E7EB' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }} data-testid="dashboard-heading">Parent Dashboard</h1>
            <p className="text-sm text-gray-600">Welcome, {user?.username}</p>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition" data-testid="logout-btn">
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {!application && !showForm && (
          <div className="card text-center py-12">
            <FileText size={64} color="#002147" className="mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#002147' }}>No Application Found</h2>
            <p className="text-gray-600 mb-6">You haven't submitted your application yet.</p>
            <button onClick={() => setShowForm(true)} className="primary-btn" data-testid="start-application-btn">
              Start Application
            </button>
          </div>
        )}

        {application && (
          <div>
            <div className="card mb-6" data-testid="application-status-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Application Status</h2>
                <span className={`status-badge status-${application.status.toLowerCase().replace(' ', '-')}`} data-testid="status-badge">
                  {application.status}
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Admission Number</p>
                  <p className="font-semibold" data-testid="admission-number">{application.admission_no}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pupil Name</p>
                  <p className="font-semibold">{application.pupil_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Class Sought</p>
                  <p className="font-semibold">{application.class_sought}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Application Date</p>
                  <p className="font-semibold">{new Date(application.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {application.interview_date && (
                <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-3">
                    <Calendar size={24} color="#7C3AED" />
                    <div>
                      <p className="font-semibold" style={{ color: '#7C3AED' }}>Interview Scheduled</p>
                      <p className="text-sm">Date: {application.interview_date} at {application.interview_time}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="card">
              <h3 className="text-xl font-bold mb-6" style={{ color: '#002147' }}>Application Details</h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-4" style={{ color: '#002147' }}>Pupil Information</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><span className="text-gray-600">Date of Birth:</span> <span className="font-medium">{application.dob}</span></div>
                    <div><span className="text-gray-600">Place of Birth:</span> <span className="font-medium">{application.place_of_birth}</span></div>
                    <div><span className="text-gray-600">Mother Tongue:</span> <span className="font-medium">{application.mother_tongue}</span></div>
                    <div><span className="text-gray-600">Vaccinated:</span> <span className="font-medium">{application.vaccinated ? 'Yes' : 'No'}</span></div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-4" style={{ color: '#002147' }}>Father's Information</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><span className="text-gray-600">Name:</span> <span className="font-medium">{application.father_name}</span></div>
                    <div><span className="text-gray-600">Occupation:</span> <span className="font-medium">{application.father_occupation}</span></div>
                    <div><span className="text-gray-600">Contact:</span> <span className="font-medium">{application.father_contact}</span></div>
                    <div><span className="text-gray-600">Email:</span> <span className="font-medium">{application.father_email}</span></div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-4" style={{ color: '#002147' }}>Mother's Information</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><span className="text-gray-600">Name:</span> <span className="font-medium">{application.mother_name}</span></div>
                    <div><span className="text-gray-600">Qualification:</span> <span className="font-medium">{application.mother_qualification}</span></div>
                    <div><span className="text-gray-600">Contact:</span> <span className="font-medium">{application.mother_contact}</span></div>
                    <div><span className="text-gray-600">Email:</span> <span className="font-medium">{application.mother_email}</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showForm && (
          <div className="card">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Admission Form</h2>
            
            <form onSubmit={handleSubmitApplication}>
              {/* Pupil Details */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#002147' }}>Pupil Details</h3>
                <p className="text-sm text-gray-600 mb-4">Admission number will be auto-generated after submission</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="form-label">Name of the Pupil *</label>
                    <input type="text" name="pupil_name" value={formData.pupil_name} onChange={handleInputChange} className="form-input" required data-testid="pupil-name-input" />
                  </div>
                  <div>
                    <label className="form-label">Date of Birth *</label>
                    <input type="date" name="dob" value={formData.dob} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Place of Birth *</label>
                    <input type="text" name="place_of_birth" value={formData.place_of_birth} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Class Sought *</label>
                    <input type="text" name="class_sought" value={formData.class_sought} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Mother Tongue *</label>
                    <input type="text" name="mother_tongue" value={formData.mother_tongue} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Medium of Instruction *</label>
                    <input type="text" name="medium_of_instruction" value={formData.medium_of_instruction} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Religion/Caste *</label>
                    <input type="text" name="religion_caste" value={formData.religion_caste} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">SATS Number (Optional)</label>
                    <input type="text" name="sats_no" value={formData.sats_no} onChange={handleInputChange} className="form-input" placeholder="Leave blank if not applicable" />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <input type="checkbox" name="vaccinated" checked={formData.vaccinated} onChange={handleInputChange} id="vaccinated" />
                    <label htmlFor="vaccinated" className="text-sm">Child is Vaccinated</label>
                  </div>
                </div>
              </div>

              {/* Father's Details */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#002147' }}>Father's Details</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Father's Name *</label>
                    <input type="text" name="father_name" value={formData.father_name} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Occupation *</label>
                    <input type="text" name="father_occupation" value={formData.father_occupation} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Annual Income *</label>
                    <input type="text" name="father_income" value={formData.father_income} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Qualification *</label>
                    <input type="text" name="father_qualification" value={formData.father_qualification} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Contact Number *</label>
                    <input type="tel" name="father_contact" value={formData.father_contact} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Email *</label>
                    <input type="email" name="father_email" value={formData.father_email} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Aadhar Number *</label>
                    <input type="text" name="father_aadhar" value={formData.father_aadhar} onChange={handleInputChange} className="form-input" required />
                  </div>
                </div>
              </div>

              {/* Mother's Details */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#002147' }}>Mother's Details</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Mother's Name *</label>
                    <input type="text" name="mother_name" value={formData.mother_name} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Qualification *</label>
                    <input type="text" name="mother_qualification" value={formData.mother_qualification} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Contact Number *</label>
                    <input type="tel" name="mother_contact" value={formData.mother_contact} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Email *</label>
                    <input type="email" name="mother_email" value={formData.mother_email} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Aadhar Number *</label>
                    <input type="text" name="mother_aadhar" value={formData.mother_aadhar} onChange={handleInputChange} className="form-input" required />
                  </div>
                </div>
              </div>

              {/* Address and General Info */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#002147' }}>General Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="form-label">Address *</label>
                    <textarea name="address" value={formData.address} onChange={handleInputChange} className="form-input" rows="3" required></textarea>
                  </div>
                  <div>
                    <label className="form-label">Number of Dependents *</label>
                    <input type="text" name="no_of_dependents" value={formData.no_of_dependents} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Pupil Staying With *</label>
                    <input type="text" name="staying_with" value={formData.staying_with} onChange={handleInputChange} className="form-input" placeholder="Parents/Guardian" required />
                  </div>
                  <div>
                    <label className="form-label">Identification Mark 1 *</label>
                    <input type="text" name="identification_mark_1" value={formData.identification_mark_1} onChange={handleInputChange} className="form-input" required />
                  </div>
                  <div>
                    <label className="form-label">Identification Mark 2 (Optional)</label>
                    <input type="text" name="identification_mark_2" value={formData.identification_mark_2} onChange={handleInputChange} className="form-input" placeholder="Optional" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="form-label">Siblings Information *</label>
                    <textarea name="siblings_info" value={formData.siblings_info} onChange={handleInputChange} className="form-input" rows="2" placeholder="Number of brothers/sisters, elder or younger" required></textarea>
                  </div>
                </div>
              </div>

              {/* Document Uploads */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#002147' }}>Document Uploads</h3>
                <p className="text-sm text-gray-600 mb-4">* Required documents | Accept: PDF, JPG, PNG (Max 5MB each)</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Birth Certificate (Optional)</label>
                    <input type="file" name="birth_certificate" onChange={handleFileChange} className="form-input" accept=".pdf,.jpg,.jpeg,.png" />
                  </div>
                  <div>
                    <label className="form-label">Child's Aadhar Copy (Optional)</label>
                    <input type="file" name="child_aadhar" onChange={handleFileChange} className="form-input" accept=".pdf,.jpg,.jpeg,.png" />
                  </div>
                  <div>
                    <label className="form-label">Father's Aadhar Copy *</label>
                    <input type="file" name="father_aadhar" onChange={handleFileChange} className="form-input" accept=".pdf,.jpg,.jpeg,.png" required />
                  </div>
                  <div>
                    <label className="form-label">Mother's Aadhar Copy *</label>
                    <input type="file" name="mother_aadhar" onChange={handleFileChange} className="form-input" accept=".pdf,.jpg,.jpeg,.png" required />
                  </div>
                  <div>
                    <label className="form-label">Passport Size Photo (Optional)</label>
                    <input type="file" name="photo" onChange={handleFileChange} className="form-input" accept=".jpg,.jpeg,.png" />
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button type="submit" disabled={submitting} className="primary-btn" data-testid="submit-application-btn">
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentDashboard;